import React, { useState, useEffect } from "react";
import { getData, postData, putData } from "../../services/api.service";
import { toastSuccess, toastError } from "../common/Toast";
import {
  FaClipboardList,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaExclamationTriangle,
  FaCheck,
  FaTimes,
  FaShieldAlt,
  FaMoneyCheckAlt
} from "react-icons/fa";

function OrderStatusManager() {
  const [orderStatuses, setOrderStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [deletingStatus, setDeletingStatus] = useState(null);
  const [showInactive, setShowInactive] = useState(false); // Toggle para mostrar inactivos
  const [formData, setFormData] = useState({
    status_description: '',
    require_control: false,
    require_payment: false,
    payment_requirement_type: 'NONE',
    active: true
  });

  useEffect(() => {
    loadOrderStatuses();
  }, []);

  const loadOrderStatuses = async () => {
    try {
      setLoading(true);      
      const response = await getData('/api/admin/get_order_status');
    
      // Inicializar como array vacío por defecto
      let statusesData = [];
      
      if (response && response.data) {
        
        // Si response.data es directamente un array
        if (Array.isArray(response.data)) {
          statusesData = response.data;
        }
        // Si está en response.data.statuses
        else if (response.data.statuses && Array.isArray(response.data.statuses)) {
          statusesData = response.data.statuses;
        }
        // Si está en response.data.status
        else if (response.data.status && Array.isArray(response.data.status)) {
          statusesData = response.data.status;
        }
        // Si está en response.data.orderStatuses
        else if (response.data.orderStatuses && Array.isArray(response.data.orderStatuses)) {
          statusesData = response.data.orderStatuses;
        }
        // Si response.data es un objeto con propiedades
        else {
          Object.keys(response.data).forEach(key => {
            if (Array.isArray(response.data[key])) {
              if (statusesData.length === 0) {
                statusesData = response.data[key];
              }
            }
          });
        }
      }
        
      // Asegurar que siempre sea un array
      if (!Array.isArray(statusesData)) {
        console.warn('statusesData no es un array, convirtiendo a array vacío');
        statusesData = [];
      }
      
      setOrderStatuses(statusesData);
    } catch (error) {
      console.error('Error cargando estados de orden:', error);
      toastError('Error al cargar los estados de orden');
      setOrderStatuses([]); // Asegurar que siempre sea un array
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      const dataToSend = {
        data: {
          status_description: formData.status_description.trim().toUpperCase(),
          require_control: formData.require_control,
          require_payment: formData.require_payment,
          payment_requirement_type: formData.payment_requirement_type,
          active: formData.active,
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      if (editingStatus) {
        dataToSend.data.id = editingStatus.id;
        await putData('/api/admin/update_order_status', dataToSend);
        toastSuccess(`Estado "${formData.status_description}" actualizado exitosamente`);
      } else {
        await postData('/api/admin/create_order_status', dataToSend);
        toastSuccess(`Estado "${formData.status_description}" creado exitosamente`);
      }
      
      setShowModal(false);
      resetForm();
      await loadOrderStatuses();
    } catch (error) {
      console.error('Error guardando estado de orden:', error);
      toastError(editingStatus ? 'Error al actualizar el estado' : 'Error al crear el estado');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingStatus(null);
    setFormData({
      status_description: '',
      require_control: false,
      require_payment: false,
      payment_requirement_type: 'NONE',
      active: true
    });
  };

  const handleEdit = (status) => {
    setEditingStatus(status);
    setFormData({
      status_description: status.status_description || '',
      require_control: status.require_control || false,
      require_payment: status.require_payment || false,
      payment_requirement_type: status.payment_requirement_type || 'NONE',
      active: status.active !== undefined ? status.active : true
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingStatus(null);
    setFormData({
      status_description: '',
      require_control: false,
      require_payment: false,
      payment_requirement_type: 'NONE',
      active: true
    });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (status) => {
    setDeletingStatus(status);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingStatus) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como inactivo (soft delete)
      const dataToSend = {
        data: {
          id: deletingStatus.id,
          status_description: deletingStatus.status_description,
          require_control: deletingStatus.require_control,
          require_payment: deletingStatus.require_payment,
          active: false, // Marcar como inactivo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      await putData('/api/admin/update_order_status', dataToSend);
      toastSuccess(`Estado "${deletingStatus.status_description}" eliminado exitosamente`);
      setShowDeleteModal(false);
      setDeletingStatus(null);
      await loadOrderStatuses();
    } catch (error) {
      console.error('Error eliminando estado:', error);
      toastError('Error al eliminar el estado');
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeletingStatus(null);
  };

  const handleReactivate = async (status) => {
    if (!status) return;
    
    try {
      setLoading(true);
      
      // Usar PUT para marcar como activo
      const dataToSend = {
        data: {
          id: status.id,
          status_description: status.status_description,
          require_control: status.require_control,
          require_payment: status.require_payment,
          payment_requirement_type: status.payment_requirement_type || 'NONE',
          active: true, // Marcar como activo
          user: 1 // TODO: Obtener del contexto de usuario actual
        }
      };
      
      await putData('/api/admin/update_order_status', dataToSend);
      toastSuccess(`Estado "${status.status_description}" reactivado exitosamente`);
      await loadOrderStatuses();
    } catch (error) {
      console.error('Error reactivando estado:', error);
      toastError('Error al reactivar el estado');
    } finally {
      setLoading(false);
    }
  };

  // Ejemplos de estados comunes
  const statusExamples = [
    'PENDIENTE',
    'EN PROCESO',
    'EN TALLER', 
    'COMPLETADO',
    'ENVIADO',
    'ENTREGADO',
    'CANCELADO',
    'DEVUELTO'
  ];

  // Filtrar estados según el toggle
  const filteredStatuses = showInactive 
    ? orderStatuses 
    : orderStatuses.filter(status => status.active);

  return (
    <div className="order-status-manager">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            <FaClipboardList className="me-2 text-primary" />
            Gestión de Estados de Orden
          </h5>
          <div className="d-flex gap-2 align-items-center">
            {/* Toggle para mostrar/ocultar inactivos */}
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                id="showInactiveToggle"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="showInactiveToggle">
                Mostrar inactivos
              </label>
            </div>
            <button
              className="btn btn-primary"
              onClick={handleNew}
              disabled={loading}
            >
              <FaPlus className="me-2" />
              Nuevo Estado
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Mensaje informativo si hay estados inactivos ocultos */}
          {!showInactive && orderStatuses.length > filteredStatuses.length && (
            <div className="alert alert-info d-flex justify-content-between align-items-center mb-3">
              <div>
                <i className="fas fa-info-circle me-2"></i>
                Hay <strong>{orderStatuses.length - filteredStatuses.length}</strong> estado(s) inactivo(s) oculto(s).
              </div>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowInactive(true)}
              >
                Mostrar todos
              </button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <div className="mt-2 text-muted">Cargando estados de orden...</div>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Descripción</th>
                    <th>Req. Control</th>
                    <th>Req. Pago</th>
                    <th>Tipo Validación Pago</th>
                    <th>Estado</th>
                    <th>Fecha Creación</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(filteredStatuses) || filteredStatuses.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center text-muted py-4">
                        {!Array.isArray(orderStatuses) ? 
                          'Error cargando estados de orden' : 
                          showInactive ? 
                            'No hay estados de orden registrados' :
                            'No hay estados activos. Activa el toggle "Mostrar inactivos" para ver estados eliminados.'
                        }
                      </td>
                    </tr>
                  ) : (
                    filteredStatuses.map(status => (
                      <tr key={status.id}>
                        <td>#{status.id}</td>
                        <td>
                          <strong className="text-uppercase">{status.status_description}</strong>
                        </td>
                        <td>
                          {status.require_control ? (
                            <span className="badge bg-warning text-dark">
                              <FaShieldAlt className="me-1" />
                              Sí
                            </span>
                          ) : (
                            <span className="badge bg-secondary">
                              <FaTimes className="me-1" />
                              No
                            </span>
                          )}
                        </td>
                        <td>
                          {status.require_payment ? (
                            <span className="badge bg-success">
                              <FaMoneyCheckAlt className="me-1" />
                              Sí
                            </span>
                          ) : (
                            <span className="badge bg-secondary">
                              <FaTimes className="me-1" />
                              No
                            </span>
                          )}
                        </td>
                        <td>
                          {status.payment_requirement_type === 'NONE' && (
                            <span className="badge bg-secondary">Sin validación</span>
                          )}
                          {status.payment_requirement_type === 'PARTIAL' && (
                            <span className="badge bg-info">Pago Parcial</span>
                          )}
                          {status.payment_requirement_type === 'FULL' && (
                            <span className="badge bg-primary">Pago Completo</span>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${status.active ? 'bg-success' : 'bg-danger'}`}>
                            {status.active ? (
                              <>
                                <FaCheck className="me-1" />
                                Activo
                              </>
                            ) : (
                              <>
                                <FaTimes className="me-1" />
                                Inactivo
                              </>
                            )}
                          </span>
                        </td>
                        <td>
                          <small className="text-muted">
                            {status.inserted_date ? new Date(status.inserted_date).toLocaleDateString('es-AR') : 'N/A'}
                          </small>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-primary me-2"
                            onClick={() => handleEdit(status)}
                            disabled={loading}
                            title="Editar estado"
                          >
                            <FaEdit />
                          </button>
                          {status.active ? (
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(status)}
                              disabled={loading}
                              title="Eliminar estado"
                            >
                              <FaTrash />
                            </button>
                          ) : (
                            <button
                              className="btn btn-sm btn-outline-success"
                              onClick={() => handleReactivate(status)}
                              disabled={loading}
                              title="Reactivar estado"
                            >
                              <FaCheck />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Card Footer con estadísticas */}
        <div className="card-footer">
          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Total de estados: {orderStatuses.length} | 
              Activos: <strong className="text-success">{orderStatuses.filter(s => s.active).length}</strong> | 
              Inactivos: <strong className="text-danger">{orderStatuses.filter(s => !s.active).length}</strong>
            </small>
            <small className="text-muted">
              Mostrando: <strong>{filteredStatuses.length}</strong> estado(s)
            </small>
          </div>
        </div>
      </div>

      {/* Modal para crear/editar estado */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingStatus ? 'Editar Estado de Orden' : 'Nuevo Estado de Orden'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  disabled={loading}
                ></button>
              </div>
              
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label className="form-label">Descripción del Estado *</label>
                        <input
                          type="text"
                          className="form-control text-uppercase"
                          name="status_description"
                          value={formData.status_description}
                          onChange={handleInputChange}
                          required
                          disabled={loading}
                          placeholder="Ej: PENDIENTE, EN PROCESO, etc."
                          style={{ textTransform: 'uppercase' }}
                        />
                        <div className="form-text">
                          Se convertirá automáticamente a mayúsculas
                        </div>
                      </div>

                      {/* Botones de ejemplos rápidos */}
                      <div className="mb-3">
                        <label className="form-label">Estados comunes:</label>
                        <div className="d-flex flex-wrap gap-1">
                          {statusExamples.map(example => (
                            <button
                              key={example}
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => setFormData(prev => ({ ...prev, status_description: example }))}
                              disabled={loading}
                            >
                              {example}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="require_control"
                            checked={formData.require_control}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            <FaShieldAlt className="me-2 text-warning" />
                            Requiere Control
                          </label>
                          <div className="form-text">
                            Indica si este estado requiere supervisión o aprobación especial
                          </div>
                        </div>
                      </div>

                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="require_payment"
                            checked={formData.require_payment}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            <FaMoneyCheckAlt className="me-2 text-success" />
                            Requiere Pago
                          </label>
                          <div className="form-text">
                            Indica si este estado requiere que el pago esté confirmado
                          </div>
                        </div>
                      </div>

                      {formData.require_payment && (
                        <div className="mb-3 ms-4">
                          <label className="form-label fw-bold">
                            Tipo de Validación de Pago *
                          </label>
                          <select
                            className="form-select"
                            name="payment_requirement_type"
                            value={formData.payment_requirement_type}
                            onChange={handleInputChange}
                            disabled={loading}
                            required
                          >
                            <option value="NONE">Sin validación (por defecto)</option>
                            <option value="PARTIAL">Pago Parcial - Al menos 1 pago registrado</option>
                            <option value="FULL">Pago Completo - Según forma de pago</option>
                          </select>
                          <div className="form-text">
                            <strong>PARTIAL:</strong> Requiere al menos un pago registrado<br/>
                            <strong>FULL:</strong> Para TC/TD valida que cliente pagó 100%, para Efectivo/Transferencia valida que esté 100% acreditado en caja
                          </div>
                        </div>
                      )}

                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="active"
                            checked={formData.active}
                            onChange={handleInputChange}
                            disabled={loading}
                          />
                          <label className="form-check-label">
                            <FaCheck className="me-2 text-success" />
                            Estado activo
                          </label>
                          <div className="form-text">
                            Los estados inactivos no aparecerán disponibles para nuevas órdenes
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                    disabled={loading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading || !formData.status_description.trim()}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Guardando...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {editingStatus ? 'Actualizar' : 'Crear'}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <FaExclamationTriangle className="text-warning me-2" />
                  Confirmar Eliminación
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={cancelDelete}
                  disabled={loading}
                ></button>
              </div>
              <div className="modal-body">
                <p>¿Estás seguro que deseas eliminar el estado <strong>"{deletingStatus?.status_description}"</strong>?</p>
                <div className="alert alert-warning">
                  <FaExclamationTriangle className="me-2" />
                  El estado será marcado como inactivo y <strong>dejará de mostrarse en el listado</strong>.
                </div>
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  Las órdenes existentes que usen este estado no se verán afectadas. Podrás ver estados eliminados activando el toggle "Mostrar inactivos".
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                  disabled={loading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Eliminando...
                    </>
                  ) : (
                    <>
                      <FaTrash className="me-2" />
                      Eliminar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrderStatusManager;
